/* eslint-disable react-hooks/exhaustive-deps */

import "yet-another-react-lightbox/dist/styles.css";

import styled, { css } from "styled-components";
import { useMemo, useState } from "react";

import ImageLoader from "./ImageLoader";
import Lightbox from "yet-another-react-lightbox";
import { clickableImageCss } from "styles/GlobalStyle/Images";
import theme from "styles/theme";

export type GalleryImageType = { src: string; name?: string };

export type GalleryImageOptionType = { index: number; aspectRatio?: number };

export enum GallerySize {
  "S" = "S",
  "M" = "M",
  "L" = "L",
  "XL" = "XL",
  "XXL" = "XXL",
  "FullWidth" = "FullWidth",
}

export enum GalleryFillRow {
  "start" = "start",
  "end" = "end",
}

const Wrapper = styled.div<{
  isSingleImage: boolean;
  fillRowAt: GalleryFillRow;
}>`
  --gap: 2px;

  width: 100%;
  display: flex;

  ${(props) =>
    props.fillRowAt === GalleryFillRow.start
      ? css`
          flex-direction: row-reverse;
          flex-wrap: wrap-reverse;
        `
      : css`
          flex-direction: row;
          flex-wrap: wrap;
        `}

  box-sizing: border-box;
  gap: var(--gap);

  ${({ isSingleImage }) =>
    isSingleImage &&
    css`
      justify-content: center;
    `}
`;

const ImageWrapper = styled.div<{
  isSingleImage;
  size: number;
  imagesPerRow: number;
  aspectRatio: number;
  imageMaxHeight: any;
}>`
  ${clickableImageCss}

  ${(props) =>
    props.size === GallerySize.FullWidth
      ? css`
          --imagesPerRow: 1;
          --singleImageMaxWidth: 100%;
        `
      : props.size === GallerySize.XXL
      ? css`
          --imagesPerRow: 2;
          --singleImageMaxWidth: 65%;
        `
      : props.size === GallerySize.XL
      ? css`
          --imagesPerRow: 3;
          --singleImageMaxWidth: 65%;
        `
      : props.size === GallerySize.L
      ? css`
          --imagesPerRow: 4;
          --singleImageMaxWidth: 65%;
        `
      : props.size === GallerySize.M
      ? css`
          --imagesPerRow: 6;
          --singleImageMaxWidth: 65%;
        `
      : props.size === GallerySize.S
      ? css`
          --imagesPerRow: 8;
          --singleImageMaxWidth: 50%;
        `
      : css`
          --imagesPerRow: 4;
          --singleImageMaxWidth: 65%;
        `};

  ${(props) =>
    props.imagesPerRow &&
    css`
      --imagesPerRow: ${props.imagesPerRow};
    `}

  --minWidth: calc(
    (100% - (var(--gap) * (var(--imagesPerRow) - 1))) / var(--imagesPerRow)
  );
  --maxWidth: 100%;
  --aspectRatio: ${(props) => props.aspectRatio};
  --flex: ${(props) => props.aspectRatio || 1};

  ${(props) =>
    props.isSingleImage &&
    css`
      --maxWidth: var(--singleImageMaxWidth);
    `};

  flex: var(--flex);
  min-width: calc(var(--flex) * var(--minWidth));
  max-width: var(--maxWidth);
  max-height: ${(props) => props.imageMaxHeight};
  aspect-ratio: var(--aspectRatio);

  box-sizing: border-box;
  justify-content: space-between;
  text-align: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const GalleryComponent = (props: {
  images: GalleryImageType[];
  imageOptions?: GalleryImageOptionType[];
  imageMaxHeight?: any;
  imagesPerRow?: number;
  size?: GallerySize;
  fillRowAt?: GalleryFillRow;
  aspectRatio?: number;
}) => {
  const { imageOptions = [], fillRowAt = GalleryFillRow.start } = props;

  const [activeImageIndex, setActiveImageIndex] = useState(undefined);

  const imagesIsReversed =
    props.images?.length > 0 && fillRowAt === GalleryFillRow.start;

  const isSingleImage = props.images?.length === 1;

  const images = useMemo(() => {
    return imagesIsReversed ? [...props.images].reverse() : props.images;
  }, [props.images, props.fillRowAt]);

  const onImageClick = (imgIndex: number) => {
    setActiveImageIndex(imgIndex);
  };

  return images?.length > 0 ? (
    <>
      <Wrapper isSingleImage={isSingleImage} fillRowAt={fillRowAt}>
        {images?.map(({ src, name }, i) => {
          const imgIndex = imagesIsReversed ? images.length - i - 1 : i;
          const imgOptions = imageOptions.find((opt) => opt.index === imgIndex);
          const aspectRatio = imgOptions?.aspectRatio || props.aspectRatio;

          return (
            <ImageWrapper
              key={i}
              onClick={() => onImageClick(imgIndex)}
              aspectRatio={aspectRatio}
              isSingleImage={isSingleImage}
              size={props.size}
              imagesPerRow={props.imagesPerRow}
              imageMaxHeight={props.imageMaxHeight}
            >
              <ImageLoader src={src} alt={name || ""} />
            </ImageWrapper>
          );
        })}
      </Wrapper>

      <Lightbox
        open={activeImageIndex !== undefined}
        index={activeImageIndex}
        close={() => setActiveImageIndex(undefined)}
        slides={props.images?.map((img) => {
          return {
            src: img.src,
            alt: img.name,
          };
        })}
        controller={{ closeOnBackdropClick: true }}
        styles={{
          container: { backgroundColor: `${theme.colors.black}EE` },
          icon: {
            color: `${theme.colors.white}`,
          },
          button: {
            filter: "none",
          },
        }}
      />
    </>
  ) : null;
};

export default GalleryComponent;
